import { useCallback } from 'react';

import { type ContextResolver, useRecommendations } from '@atlassian/app-recommendations-kit';

const SNOOZE_INTERVALS = {
	0: 7,
	1: 14,
	2: 30,
	3: 90,
};

export const useMsrSnoozeHandler = ({
	setFeatureVisited,
	getFeatureVisitedCount,
	setFeatureVisitedCount,
}: {
	getFeatureVisitedCount: () => number;
	setFeatureVisited: (days: number) => void;
	setFeatureVisitedCount: (count: number) => void;
}): {
	blockEligibilityToPreventRace: () => void;
	blockEligibilityUntilTomorrow: () => void;
	snooze: () => void;
} => {
	const blockEligibilityToPreventRace = useCallback((): void => {
		setFeatureVisited(0.1);
	}, [setFeatureVisited]);

	const blockEligibilityUntilTomorrow = useCallback((): void => {
		setFeatureVisited(1);
	}, [setFeatureVisited]);

	const snooze = useCallback((): void => {
		const visitedCount = getFeatureVisitedCount();
		// We are going to loop the last nudge indefinitely
		const loopedVisitedCount = Math.min(visitedCount, Object.keys(SNOOZE_INTERVALS).length - 1);
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		const snoozeInterval = SNOOZE_INTERVALS[loopedVisitedCount as keyof typeof SNOOZE_INTERVALS];
		setFeatureVisited(snoozeInterval);
		setFeatureVisitedCount(visitedCount + 1);
	}, [setFeatureVisited, setFeatureVisitedCount, getFeatureVisitedCount]);

	return { snooze, blockEligibilityUntilTomorrow, blockEligibilityToPreventRace };
};

const MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE = 'crossJoinNudgeFlags';

export class RecommendationError extends Error {
	options: { cause: Error };

	constructor(message: string, options: { cause: Error }) {
		super(message);
		this.name = 'RecommendationError';
		this.options = options;
	}
}

export function useMSRNudgeRecommendations({
	product,
	sessionId,
	cloudId,
	subProduct,
}: {
	cloudId: string;
	product: string;
	sessionId: string;
	subProduct: string;
}): () => Promise<string[]> {
	const contextResolver: ContextResolver = useCallback(
		() => ({
			product,
			useCase: MSR_CROSS_JOIN_NUDGE_RECOMMENDATIONS_USE_CASE,
			tenantId: cloudId,
			subproduct: subProduct,
			sessionId,
		}),
		[cloudId, product, subProduct, sessionId],
	);

	const { getRecommendations } = useRecommendations({ contextResolver });

	return useCallback(async (): Promise<string[]> => {
		try {
			const result = await getRecommendations({
				first: 4,
				config: {
					cacheConfig: {
						ttl: 86400, // cache for 1 day in seconds
					},
				},
			});

			const { response: { data } = {} } = result;

			const recommendedEntities = (data ?? []).map((e) => e.entityId);

			return recommendedEntities;
		} catch (error: any) {
			let cause;
			if (error instanceof Error) {
				cause = error;
			} else if (error && error.reason) {
				cause = new Error(error.reason);
			} else {
				cause = new Error(String(error));
			}

			throw new RecommendationError('Get MSR nudge recommendations error', {
				cause,
			});
		}
	}, [getRecommendations]);
}
